import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Card,
} from "@mui/material";

import {
  Close as CloseIcon,
  RemoveRedEye as ViewsIcon,
  TouchApp as ClicksIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";

const AdDetailsModal = ({ open, onClose, ad }) => {
  if (!ad) return null;

  // Normalize backend → frontend fields
  const mapped = {
    id: ad.id,
    title: ad.title,
    description: ad.description,
    status: ad.status,
    adType: ad.ad_type,
    targetUrl: ad.click_url,
    imageUrl: ad.metadata?.image_url || null, // still available, but NOT displayed
    impressions: ad.impressions || 0,
    clicks: ad.clicks || 0,
    impressionGoal: ad.impressions_goal,
    createdAt: ad.created_at,
    updatedAt: ad.updated_at,
    startDate: ad.start_date,
    endDate: ad.end_date,
  };

  const ctr = mapped.impressions
    ? ((mapped.clicks / mapped.impressions) * 100).toFixed(2)
    : "0.00";

  const progress = mapped.impressionGoal
    ? Math.min((mapped.impressions / mapped.impressionGoal) * 100, 100).toFixed(
        1
      )
    : null;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "paused":
        return "warning";
      case "completed":
        return "info";
      case "draft":
      default:
        return "default";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const linkStyle = {
    fontWeight: 600,
    color: "primary.main",
    cursor: "pointer", // 👈 hand cursor
    textDecoration: "underline",
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {/* HEADER */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Ad Details
          </Typography>
          <Chip
            label={mapped.status}
            color={getStatusColor(mapped.status)}
            size="small"
            sx={{ textTransform: "capitalize" }}
          />
        </Box>

        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* BODY */}
      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={3}>
          {/* TITLE + DESCRIPTION */}
          <Grid item xs={12}>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              {mapped.title}
            </Typography>

            <Typography variant="body1" color="text.secondary">
              {mapped.description}
            </Typography>
          </Grid>

          {/* PERFORMANCE METRICS */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Performance Metrics
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Card
                  sx={{ p: 2, textAlign: "center", bgcolor: "primary.light" }}
                >
                  <ViewsIcon
                    sx={{ fontSize: 40, color: "primary.main", mb: 1 }}
                  />
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {mapped.impressions.toLocaleString()}
                  </Typography>
                  <Typography variant="body2">Total Impressions</Typography>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card
                  sx={{ p: 2, textAlign: "center", bgcolor: "success.light" }}
                >
                  <ClicksIcon
                    sx={{ fontSize: 40, color: "success.main", mb: 1 }}
                  />
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {mapped.clicks.toLocaleString()}
                  </Typography>
                  <Typography variant="body2">Total Clicks</Typography>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card
                  sx={{ p: 2, textAlign: "center", bgcolor: "warning.light" }}
                >
                  <TrendingUpIcon
                    sx={{ fontSize: 40, color: "warning.main", mb: 1 }}
                  />
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {ctr}%
                  </Typography>
                  <Typography variant="body2">Click-Through Rate</Typography>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* GOAL PROGRESS */}
          {mapped.impressionGoal && (
            <Grid item xs={12}>
              <Box sx={{ p: 3, bgcolor: "grey.50", borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Impression Goal Progress — {progress}%
                </Typography>

                <Box
                  sx={{
                    mt: 1,
                    height: 12,
                    bgcolor: "grey.200",
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      width: `${progress}%`,
                      bgcolor: "primary.main",
                    }}
                  />
                </Box>

                <Typography variant="body2" sx={{ mt: 1 }}>
                  {mapped.impressions.toLocaleString()} /{" "}
                  {mapped.impressionGoal.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* AD INFO */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Ad Information
            </Typography>

            <Grid container spacing={2}>
              {/* TYPE */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption">Ad Type</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {mapped.adType}
                </Typography>
              </Grid>

              {/* CREATED */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption">Created At</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formatDate(mapped.createdAt)}
                </Typography>
              </Grid>

              {/* TARGET URL */}
              <Grid item xs={12}>
                <Typography variant="caption">Target URL</Typography>
                <Typography
                  variant="body1"
                  sx={linkStyle}
                  onClick={() => window.open(mapped.targetUrl, "_blank")}
                >
                  {mapped.targetUrl}
                </Typography>
              </Grid>

              {/* IMAGE URL: still clickable, no preview */}
              {mapped.imageUrl && (
                <Grid item xs={12}>
                  <Typography variant="caption">Image URL</Typography>
                  <Typography
                    variant="body1"
                    sx={linkStyle}
                    onClick={() => window.open(mapped.imageUrl, "_blank")}
                  >
                    {mapped.imageUrl}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>

          {/* FOOTER */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption">Last Updated</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatDate(mapped.updatedAt)}
            </Typography>

            <Typography variant="caption" sx={{ mt: 2, display: "block" }}>
              Ad ID
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontFamily: "monospace", fontWeight: 600 }}
            >
              {mapped.id}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>

      {/* FOOTER BUTTON */}
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdDetailsModal;
